import { Search, SlidersHorizontal } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import "./SearchFilter.css";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../redux/snackbarSlice";
import useClickOutside from "../../hooks/useClickOutside";

const SearchFilter = ({
  searchQuery,
  setSearchQuery,
  columnOptions = [],
  setSearchType,
  searchType,
  setSelectedColumns,
  selectedColumns,
  disableSearchFilters = false
}) => {

  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const columnOptionsRef = useRef(null);
  const searchDropdownRef = useRef(null);

  const dispatch = useDispatch();
  const snackbar = (message,type) => {
    dispatch(showSnackbar({type:type,message:message}))
  }

  const searchOptions = [
    { field: "all_columns", headName: "All Columns", type: "radio" },
    { field: "select_columns", headName: "Select Columns", type: "radio" },
  ];

  const isAnyColumnSelected = Object.values(selectedColumns).some(value => value);
  const isFilterActive = searchType === "select_columns" && isAnyColumnSelected;

  // Use the useClickOutside hook to handle clicks outside the wrapper
  // But exclude clicks on the search input and filter icon (handled separately)
  useClickOutside(dropdownRef, () => {
    if (showSearchDropdown) {
      setShowSearchDropdown(false);
    }
  });

  // Calculate and set max-height for dropdown and column-options based on available space
  useEffect(() => {
    if (showSearchDropdown && searchDropdownRef.current && columnOptionsRef.current) {
      const calculateMaxHeight = () => {
        const dropdown = searchDropdownRef.current;
        const columnOptions = columnOptionsRef.current;
        
        if (!dropdown || !columnOptions) return;

        // Get dropdown position relative to viewport
        const dropdownRect = dropdown.getBoundingClientRect();
        const dropdownTop = dropdownRect.top;
        
        // Validate dropdown position (must be visible)
        if (dropdownTop < 0 || dropdownTop > window.innerHeight) {
          // Dropdown is outside viewport, use a safe default
          const safeHeight = Math.min(400, window.innerHeight * 0.6);
          columnOptions.style.setProperty('max-height', `${safeHeight}px`, 'important');
          dropdown.style.setProperty('max-height', `${safeHeight + 100}px`, 'important');
          return;
        }
        
        // Find the closest popup container first (for popup contexts like PartMentionsPopup)
        let containerBottom = window.innerHeight;
        let containerElement = dropdown.closest('.popup-card') || dropdown.closest('.popup-body') || dropdown.closest('.popup-overlay');
        
        if (containerElement) {
          // We're inside a popup - use popup container boundaries
          const containerRect = containerElement.getBoundingClientRect();
          containerBottom = containerRect.bottom;
          
          // Ensure container bottom doesn't exceed viewport and is valid
          containerBottom = Math.min(containerBottom, window.innerHeight);
          
          // If popup bottom is above dropdown, something's wrong - use viewport
          if (containerBottom <= dropdownTop) {
            containerBottom = window.innerHeight;
          }
        } else {
          // We're NOT inside a popup (like in CopyReplace page) - use viewport bottom
          // The dropdown should not extend beyond the visible viewport
          containerBottom = window.innerHeight;
        }
        
        // Calculate available space from dropdown top to container bottom
        // Leave padding for better UX (30px from bottom to ensure scroll works properly)
        const paddingFromBottom = 30;
        const rawAvailableSpace = containerBottom - dropdownTop - paddingFromBottom;
        
        // Ensure minimum available space for scrolling to work (at least 150px)
        // Also cap at reasonable maximum (80% of viewport height)
        const maxAllowed = window.innerHeight * 0.8;
        const availableSpace = Math.max(150, Math.min(maxAllowed, rawAvailableSpace));
        
        // Get the height of search-options section (radio buttons)
        const searchOptionsElement = dropdown.querySelector('.search-options');
        const searchOptionsHeight = searchOptionsElement ? Math.max(60, searchOptionsElement.offsetHeight) : 80;
        
        // Calculate max height for column-options 
        // (available space minus search options height minus extra padding for scroll)
        const extraPadding = 30; // Extra padding to ensure last items are fully visible
        const maxColumnOptionsHeight = Math.max(150, availableSpace - searchOptionsHeight - extraPadding);
        
        // Set max-height for column-options dynamically with !important to override CSS
        columnOptions.style.setProperty('max-height', `${maxColumnOptionsHeight}px`, 'important');
        
        // Also set max-height for dropdown container to ensure proper scrolling
        // This should be slightly larger than the column-options to account for padding
        const dropdownMaxHeight = Math.max(200, searchOptionsHeight + maxColumnOptionsHeight + 20);
        dropdown.style.setProperty('max-height', `${dropdownMaxHeight}px`, 'important');
      };

      // Calculate on mount and when dropdown opens - use multiple methods to ensure DOM is ready
      // Use requestAnimationFrame to ensure calculation happens after browser paint
      const rafId = requestAnimationFrame(() => {
        calculateMaxHeight();
        // Also recalculate after a short delay to ensure everything is settled
        setTimeout(calculateMaxHeight, 0);
        setTimeout(calculateMaxHeight, 10);
        setTimeout(calculateMaxHeight, 50);
      });
      
      const timeoutId1 = setTimeout(calculateMaxHeight, 0);
      const timeoutId2 = setTimeout(calculateMaxHeight, 10);
      const timeoutId3 = setTimeout(calculateMaxHeight, 50);

      // Recalculate on window resize or scroll
      const handleResize = () => {
        // Debounce resize handler slightly
        setTimeout(calculateMaxHeight, 10);
      };
      const handleScroll = () => {
        // Debounce scroll handler slightly
        setTimeout(calculateMaxHeight, 10);
      };

      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true); // Use capture to catch all scroll events
      
      // Also listen for popup body scroll if it exists
      const popupBody = searchDropdownRef.current?.closest('.popup-body');
      if (popupBody) {
        popupBody.addEventListener('scroll', handleScroll);
      }

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
        if (popupBody) {
          popupBody.removeEventListener('scroll', handleScroll);
        }
        cancelAnimationFrame(rafId);
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        clearTimeout(timeoutId3);
      };
    } else {
      // Reset max-height when dropdown is closed
      if (columnOptionsRef.current) {
        columnOptionsRef.current.style.removeProperty('max-height');
      }
      if (searchDropdownRef.current) {
        searchDropdownRef.current.style.removeProperty('max-height');
      }
    }
  }, [showSearchDropdown]);

  // Add local state for debounced input
  const [inputValue, setInputValue] = useState(searchQuery);

  // Keep inputValue in sync with searchQuery from parent
  useEffect(() => {
    setInputValue(searchQuery);
  }, [searchQuery]);

  // Debounce effect
  useEffect(() => {
    const handler = setTimeout(() => {
      // inputRef.current?.blur()
      setSearchQuery(inputValue);
    }, 500); // 1300 debounce

    return () => clearTimeout(handler);
  }, [inputValue, setSearchQuery]);

  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    if (type === "all_columns") {
      // Select all columns when switching to "All Columns"
      const allColumnsSelected = {};
      columnOptions.forEach((column) => {
        const id = column.columnKey || column.field;
        allColumnsSelected[id] = true;
      });
      setSelectedColumns(allColumnsSelected);
    }
  };

  const handleColumnToggle = (columnId, event) => {
    // Prevent double-toggling when clicking directly on checkbox
    if (event && event.target.type === 'checkbox') {
      event.stopPropagation();
    }
    
    if(searchType!== "all_columns"){
      
      setSelectedColumns((prev) => {
        let updated = { ...prev,[columnId]: !prev[columnId]}
        if(Object.values(updated).filter(c=>c===true).length===0){
          snackbar("Please keep at least one column selected to apply the search filter.","error")
          return prev
        }else{  
          return updated;
        }
      });
    }
  };

  
  return (
    <div 
      className="search-bar-wrapper" 
      ref={dropdownRef}
      onClick={(e) => {
        // Close dropdown when clicking on area above search bar (wrapper area)
        const target = e.target;
        const isSearchBar = target.closest('.search-bar');
        const isDropdown = target.closest('.search-dropdown');
        
        // Only close if clicking on wrapper area (above search bar), not on search bar or dropdown
        if (!isSearchBar && !isDropdown && showSearchDropdown) {
          setShowSearchDropdown(false);
        }
      }}
    >
      <div className="search-bar">
        <div
          className={`search-icon-left ${showSearchDropdown || isFilterActive ? "active" : ""}`}
          style={{cursor:disableSearchFilters ? "no-drop" :"pointer"}}
          onClick={(e) => {
            if(disableSearchFilters){
              return
            }
            e.stopPropagation();
            // Toggle dropdown when clicking the icon
            setShowSearchDropdown(!showSearchDropdown);
          }}
        >
          <SlidersHorizontal
            size={18}
            color={showSearchDropdown || isFilterActive ? "#0D6EFD" : "#fff"}
          />
        </div>

        <input
          type="text"
          placeholder="Type to Search"
          className="search-input"
          style={{cursor:disableSearchFilters ? "no-drop" :"pointer"}}
          ref={inputRef}
          onClick={(e) => {
            e.stopPropagation();
            if (showSearchDropdown) {
              setShowSearchDropdown(false);
            }
          }}
          onFocus={() => {
            if (showSearchDropdown) {
              setShowSearchDropdown(false);
            }
          }}
          value={inputValue} 
          onChange={e => setInputValue(e.target.value)}
          disabled={disableSearchFilters}
        />

        <div 
          className="search-icon-right"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {
            searchQuery.length===0 ? <>
              <Search size={18} color="#999" />
            </>:
            <span style={{cursor:"pointer"}} onClick={()=>setSearchQuery("")} className="app-table-cancel-search">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.4 14L0 12.6L5.6 7L0 1.4L1.4 0L7 5.6L12.6 0L14 1.4L8.4 7L14 12.6L12.6 14L7 8.4L1.4 14Z" fill="#6C757D"/>
              </svg>
            </span>
          }
        </div>
      </div>

      {showSearchDropdown && (
        <div className="search-dropdown" ref={searchDropdownRef}>
          <div className="search-options">
            {searchOptions.map((option) => (
              <div 
                key={option.field} 
                className="search-option"
                onClick={() => handleSearchTypeChange(option.field)}
              >
                <input
                  type="radio"
                  id={option.field}
                  name="searchType"
                  checked={searchType === option.field}
                  onChange={() => handleSearchTypeChange(option.field)}
                />
                <label htmlFor={option.field}>{option.headName}</label>
              </div>
            ))}
          </div>

          <div className="column-options" ref={columnOptionsRef}>
            {columnOptions.map((column) => {
              const columnKey = column.columnKey || column.field;
              return (
                <div 
                  key={columnKey} 
                  className="column-option"
                  onClick={(e) => {
                    // Only handle click if it's not directly on the checkbox or label
                    // (label clicks will trigger checkbox onChange via htmlFor)
                    if (e.target.type !== 'checkbox' && e.target.tagName !== 'LABEL') {
                      handleColumnToggle(columnKey, e);
                    }
                  }}
                >
                  {/* Filter column checkbox */}
                  <input
                    type="checkbox"
                    id={columnKey}
                    checked={selectedColumns[columnKey] || searchType === "all_columns"}
                    onChange={(e) => handleColumnToggle(columnKey, e)}
                    disabled={searchType === "all_columns"}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {/* Filter column label */}
                  <label 
                    htmlFor={columnKey}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {column.headName}
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchFilter;
